import { useState, useEffect, useRef, useMemo } from 'react'
import {
  getModelos,
  getInventariosByModelo,
  getInventarioProyectadoByModelo,
  calcularGuardarInventarioMensual,
  actualizarCargabilidadCompra,
  setEscenarioCaracteristica,
} from 'src/services/motosSnopServices'
import {
  getCampoBasePorTipoDato,
  getTiposDatosPlanificacion,
  getValorCeldaPlanificacion,
} from 'src/constants/compraPlanificacionFilas'

const STORAGE_KEY_PROTO_SEG = 'compra_prototipos_segundas'
const LOCAL_EVENT_PROTO_SEG = 'compraProtoSegChange'

const getStoredProtoSeg = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_PROTO_SEG)
    if (!stored) return { prototipos: false, segundas: false }
    const parsed = JSON.parse(stored)
    return {
      prototipos: Boolean(parsed?.prototipos),
      segundas: Boolean(parsed?.segundas),
    }
  } catch {
    return { prototipos: false, segundas: false }
  }
}

export const useDetalleCompra = (initialParams = {}, escenario = 'inicial_2025_6', tipoNegocio = 'ASAS') => {
  // Estados principales
  const [showAlert, setShowAlert] = useState(false)
  const [alertMessage, setAlertMessage] = useState('')
  const [companiaSelected, setCompaniaSelected] = useState(tipoNegocio || initialParams.compania || '')
  const [modelos, setModelos] = useState([])
  const [loadingModelos, setLoadingModelos] = useState(true)
  const [modeloSlected, setModeloSlected] = useState(initialParams.modelo || null)
  const [inventarioData, setInventarioData] = useState(null)
  const [loadingInventario, setLoadingInventario] = useState(false)
  const [tablaData, setTablaData] = useState([])
  const [loadingTabla, setLoadingTabla] = useState(false)
  const [nivelServicio, setNivelServicio] = useState('')
  const [mesLlegada, setMesLlegada] = useState('')
  const [cargabilidad, setCargabilidad] = useState('')
  const [fobPorMes, setFobPorMes] = useState({})
  const [mesesColumnas, setMesesColumnas] = useState([])
  const [tipoPeriodo, setTipoPeriodo] = useState('mes')
  const [prototipos, setPrototipos] = useState(() => getStoredProtoSeg().prototipos)
  const [segundas, setSegundas] = useState(() => getStoredProtoSeg().segundas)
  const skipPrototiposEffectOnce = useRef(true)
  
  // Estado para modal de llegadas
  const [isLlegadasModalOpen, setIsLlegadasModalOpen] = useState(false)
  const [optimizacionTecnoSuper, setOptimizacionTecnoSuper] = useState(false)
  const [loadingOptimizacionTecnoSuper, setLoadingOptimizacionTecnoSuper] =
    useState(false)

  // Opciones de compañías disponibles
  const companiasDisponibles = ['ASAS', 'MOBILITY']
  
  // Opciones de tipo de período
  const tiposPeriodoDisponibles = [
    { value: 'mes', label: 'Mes' },
    { value: 'semana', label: 'Semana' }
  ]

  // Actualizar companiaSelected cuando cambie tipoNegocio
  useEffect(() => {
    setCompaniaSelected(tipoNegocio)
  }, [tipoNegocio])

  // Persistir y compartir estado de prototipos/segundas entre secciones
  useEffect(() => {
    try {
      const payload = { prototipos, segundas }
      localStorage.setItem(STORAGE_KEY_PROTO_SEG, JSON.stringify(payload))
      window.dispatchEvent(new CustomEvent(LOCAL_EVENT_PROTO_SEG, { detail: payload }))
    } catch {
      // Ignorar errores de storage
    }
  }, [prototipos, segundas])

  // Sincronizar cambios desde otras secciones/pestañas
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key !== STORAGE_KEY_PROTO_SEG || !e.newValue) return
      try {
        const parsed = JSON.parse(e.newValue)
        setPrototipos(Boolean(parsed?.prototipos))
        setSegundas(Boolean(parsed?.segundas))
      } catch {
        // Ignorar payload inválido
      }
    }

    const handleLocalEvent = (e) => {
      const detail = e?.detail || {}
      setPrototipos(Boolean(detail.prototipos))
      setSegundas(Boolean(detail.segundas))
    }

    window.addEventListener('storage', handleStorage)
    window.addEventListener(LOCAL_EVENT_PROTO_SEG, handleLocalEvent)
    return () => {
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener(LOCAL_EVENT_PROTO_SEG, handleLocalEvent)
    }
  }, [])

  // Cargar modelos al inicializar o cuando cambien los initialParams
  useEffect(() => {
    const loadModelos = async () => {
      setLoadingModelos(true)
      try {
        // 1. Si se proporcionan modelos desde el botón "Ver", usarlos
        if (initialParams?.modelosFiltrados !== undefined && initialParams.modelosFiltrados.length >= 0) {
          setModelos(initialParams.modelosFiltrados)
          setLoadingModelos(false)
          return
        }
        
        // 2. Intentar cargar desde localStorage
        const storedModelos = localStorage.getItem('resumenCompra_modelosFiltrados')
        if (storedModelos !== null) {
          try {
            const modelosParsed = JSON.parse(storedModelos)
            setModelos(Array.isArray(modelosParsed) ? modelosParsed : [])
            setLoadingModelos(false)
            return
          } catch (error) {
            console.error('Error al parsear modelos desde localStorage:', error)
          }
        }
        
        // 3. Si no hay datos guardados, cargar todos los modelos
        const response = await getModelos(companiaSelected)
        if (response?.modelos) {
          setModelos(response.modelos)
        } else {
          setModelos([])
        }
      } catch (error) {
        console.error('Error al cargar modelos:', error)
        setModelos([])
      } finally {
        setLoadingModelos(false)
      }
    }
    
    loadModelos()
  }, [initialParams, companiaSelected])

  // Funciones de utilidad
  const formatearFecha = (fechaString) => {
    if (!fechaString) return ''
    const fecha = new Date(fechaString)
    const año = fecha.getFullYear()
    const mes = (fecha.getMonth() + 1).toString().padStart(2, '0')
    const dia = fecha.getDate().toString().padStart(2, '0')
    return `${año}-${mes}-${dia}`
  }

  const formatearNivelServicio = (nivelString) => {
    if (!nivelString) return ''
    const nivel = parseFloat(nivelString)
    return nivel.toFixed(2)
  }

  const generarOpcionesFechas = () => {
    const opciones = []
    const fechaActual = new Date()

    for (let i = -12; i <= 12; i += 1) {
      const fecha = new Date(
        fechaActual.getFullYear(),
        fechaActual.getMonth() + i,
        10,
      )
      const año = fecha.getFullYear()
      const mes = (fecha.getMonth() + 1).toString().padStart(2, '0')
      const dia = '10'
      const fechaFormateada = `${año}-${mes}-${dia}`
      opciones.push(fechaFormateada)
    }
    return opciones
  }

  const encontrarFechaCercana = (fechaObjetivo) => {

    if (!fechaObjetivo) return ''

    const opcionesFechas = generarOpcionesFechas()
    const fechaObj = new Date(fechaObjetivo)

    const fechaFormateada = formatearFecha(fechaObjetivo)
    if (opcionesFechas.includes(fechaFormateada)) {
      return fechaFormateada
    }

    let fechaCercana = opcionesFechas[0]
    let menorDiferencia = Math.abs(new Date(opcionesFechas[0]) - fechaObj)

    opcionesFechas.forEach((opcion) => {
      const diferencia = Math.abs(new Date(opcion) - fechaObj)
      if (diferencia < menorDiferencia) {
        menorDiferencia = diferencia
        fechaCercana = opcion
      }
    })


    return fechaObjetivo
  }

  // Funciones de carga de datos
  const tieneHighlightCampo = (inv, campoBase) => {
    if (!inv || !campoBase) return false
    const p =
      prototipos &&
      !Number.isNaN(parseFloat(inv[`${campoBase}_prototipos`])) &&
      parseFloat(inv[`${campoBase}_prototipos`]) > 0
    const s =
      segundas &&
      !Number.isNaN(parseFloat(inv[`${campoBase}_segundas`])) &&
      parseFloat(inv[`${campoBase}_segundas`]) > 0
    return p || s
  }

  const fetchInventarioData = async (modeloPlaneacion) => {
    setLoadingInventario(true)
    try {
      const response = await getInventariosByModelo(
        modeloPlaneacion,
        prototipos,
        segundas,
      )
      if (response && response.inventarios && response.inventarios.length > 0) {
        const inventario = response.inventarios[0]
        setInventarioData({
          aplica_consignacion: inventario.aplica_consignacion,
          inventario_total: parseFloat(inventario.inventario_total) || 0,
          inventario_red: parseFloat(inventario.inventario_red) || 0,
          inventario_real_ckd: parseFloat(inventario.inventario_real_ckd) || 0,
          inventario_real_pt: parseFloat(inventario.inventario_real_pt) || 0,
          promedio_sell_out_ultimos_3_meses: parseFloat(inventario.promedio_sell_out_ultimos_3_meses) || 0,
          inventario_real_control_calidad:
            parseFloat(inventario.inventario_real_control_calidad) || 0,
          inventario_real_bloqueado: parseFloat(inventario.inventario_real_bloqueado) || 0,
          inventario_real_consignacion:
            parseFloat(inventario.inventario_real_consignacion) || 0,
          inventarioSocios: 0,
          promedio3MSellOut: parseFloat(inventario.sell_out) || 0,
          bias_unidades: inventario.bias_unidades,
          bias: inventario.bias,
          mape_unidades: inventario.mape_unidades,
          mape: inventario.mape,
          highlightInventario: {
            total: tieneHighlightCampo(inventario, 'inventario_total'),
            ckd: tieneHighlightCampo(inventario, 'inventario_real_ckd'),
            ensamblado: tieneHighlightCampo(inventario, 'inventario_real_pt'),
            controlCalidad: tieneHighlightCampo(
              inventario,
              'inventario_real_control_calidad',
            ),
            bloqueado: tieneHighlightCampo(inventario, 'inventario_real_bloqueado'),
            consignacion: tieneHighlightCampo(
              inventario,
              'inventario_real_consignacion',
            ),
            inventarioSocios: tieneHighlightCampo(inventario, 'inventario_red'),
            promedio3MSellOut: tieneHighlightCampo(
              inventario,
              'promedio_sell_out_ultimos_3_meses',
            ),
          },
        })
      } else {
        setInventarioData(null)
        setAlertMessage('No se encontraron datos de inventario para este modelo')
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error al cargar datos de inventario:', error)
      setInventarioData(null)
      setAlertMessage('Error al cargar datos de inventario')
      setShowAlert(true)
    } finally {
      setLoadingInventario(false)
    }
  }

  const fetchTablaData = async (modeloPlaneacion, periodoOverride = null, preservarFiltros = false) => {
    setLoadingTabla(true)
    try {
      const periodoAUsar = periodoOverride || tipoPeriodo
      const response = await getInventarioProyectadoByModelo(
        modeloPlaneacion,
        escenario,
        periodoAUsar,
        prototipos,
        segundas,
      )
      if (response && response.success && response.success.length > 0) {
        console.log(
          '[fetchTablaData] Sell Out Tecnosuper por periodo:',
          response.success.map((item) => ({
            mes: item.mes,
            anio: item.anio,
            semana_iso: item.semana_iso,
            anio_iso: item.anio_iso,
            sell_out_tecnosuper: item.sell_out_tecnosuper,
          })),
        )

        // Extraer cargabilidad del primer elemento
        const primerElemento = response.success[0]
        if (primerElemento && primerElemento.cargabilidad !== undefined) {
          setCargabilidad(primerElemento.cargabilidad)
        }
        
        if (response.success.length > 1) {
          const segundoElemento = response.success[1]
          if (segundoElemento && !preservarFiltros) {
            setNivelServicio(
              formatearNivelServicio(segundoElemento.dias_inventario),
            )
            setMesLlegada(
              encontrarFechaCercana(
                segundoElemento.fecha_disponible_para_facturacion,
              ),
            )
          }
        }

        const tiposDatos = getTiposDatosPlanificacion()
        const campoBasePorTipoDato = getCampoBasePorTipoDato()

        const aplicaConsignacionEnPeriodo = (d) => {
          const aplica = d?.aplica_consignacion
          return (
            aplica === 1 ||
            aplica === '1' ||
            aplica === true ||
            (aplica != null && !Number.isNaN(Number(aplica)) && Number(aplica) === 1)
          )
        }

        // El API no manda inventario_final_un_pv_real_prototipos/segundas; usa mb5b_* según consignación
        const tieneHighlightInventarioFinalUnPvReal = (d) => {
          if (!d) return false
          const base = aplicaConsignacionEnPeriodo(d)
            ? 'mb5b_inventario_con_consignacion'
            : 'mb5b_inventario_sin_consignacion'
          const valorPrototipos = parseFloat(d[`${base}_prototipos`])
          const valorSegundas = parseFloat(d[`${base}_segundas`])
          const prototiposMayorCero =
            prototipos && !Number.isNaN(valorPrototipos) && valorPrototipos > 0
          const segundasMayorCero = segundas && !Number.isNaN(valorSegundas) && valorSegundas > 0
          return prototiposMayorCero || segundasMayorCero
        }

        const tieneValorEspecial = (datosDelPeriodo, campoBase) => {
          if (!datosDelPeriodo || !campoBase) return false
          if (campoBase === 'inventario_final_un_pv_real') {
            return tieneHighlightInventarioFinalUnPvReal(datosDelPeriodo)
          }
          const valorPrototipos = parseFloat(datosDelPeriodo[`${campoBase}_prototipos`])
          const valorSegundas = parseFloat(datosDelPeriodo[`${campoBase}_segundas`])
          const prototiposMayorCero = prototipos && !Number.isNaN(valorPrototipos) && valorPrototipos > 0
          const segundasMayorCero = segundas && !Number.isNaN(valorSegundas) && valorSegundas > 0
          return prototiposMayorCero || segundasMayorCero
        }

        const datosPorPeriodo = {}
        const periodosDisponibles = new Set()
        const fobMap = {}

        response.success.forEach((item) => {
          if (
            item.mes != null &&
            item.anio != null &&
            item.fob_llegadas_unidades_totales != null &&
            item.fob_llegadas_unidades_totales !== ''
          ) {
            fobMap[`${Number(item.mes)}-${Number(item.anio)}`] =
              item.fob_llegadas_unidades_totales
          }
          let periodoKey
          if (periodoAUsar === 'semana') {
            // Para semanas: usar semana_iso y anio_iso
            periodoKey = `${item.semana_iso}-${item.anio_iso}`
          } else {
            // Para meses: usar mes y anio
            periodoKey = `${item.mes}-${item.anio}`
          }
          datosPorPeriodo[periodoKey] = item
          periodosDisponibles.add(periodoKey)
        })

        const periodosOrdenados = Array.from(periodosDisponibles).sort((a, b) => {
          const [periodoA, añoA] = a.split('-').map(Number)
          const [periodoB, añoB] = b.split('-').map(Number)
          return añoA - añoB || periodoA - periodoB
        })

        const columnasGeneradas = periodosOrdenados.map((periodoKey) => {
          const [periodo, año] = periodoKey.split('-')
          
          if (periodoAUsar === 'semana') {
            // Para semanas: formato "01-2026", "02-2026", etc.
            const semanaFormateada = periodo.toString().padStart(2, '0')
            return `${semanaFormateada}-${año}`
          } 
            // Para meses: formato "ene-26", "feb-26", etc.
            const nombresMeses = [
              'ene', 'feb', 'mar', 'abr', 'may', 'jun',
              'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
            ]
            const nombreMes = nombresMeses[parseInt(periodo, 10) - 1]
            const añoCorto = año.slice(-2)
            return `${nombreMes}-${añoCorto}`
          
        })

        const tablaDataFromAPI = tiposDatos.map((tipo, index) => {
          const fila = { id: index + 1, tipoDato: tipo }
          const camposResaltados = {}

          periodosOrdenados.forEach((periodoKey, idx) => {
            const nombreColumna = columnasGeneradas[idx]
            const datosDelPeriodo = datosPorPeriodo[periodoKey]

            if (datosDelPeriodo) {
              const campoBase = campoBasePorTipoDato[tipo]
              camposResaltados[nombreColumna] = tieneValorEspecial(datosDelPeriodo, campoBase)
              fila[nombreColumna] = getValorCeldaPlanificacion(tipo, datosDelPeriodo)
            }
          })

          fila.__highlightFlags = camposResaltados
          return fila
        })

        setMesesColumnas(columnasGeneradas)
        setTablaData(tablaDataFromAPI)
        setFobPorMes(fobMap)
      } else {
        setTablaData([])
        setNivelServicio('')
        setMesLlegada('')
        setCargabilidad('')
        setFobPorMes({})
      }
    } catch (error) {
      console.error('Error al cargar datos de la tabla:', error)
      setTablaData([])
      setNivelServicio('')
      setMesLlegada('')
      setCargabilidad('')
      setFobPorMes({})
    } finally {
      setLoadingTabla(false)
    }
  }

  // Cargar datos cuando se inicializa con parámetros
  useEffect(() => {
    if (initialParams?.compania && initialParams?.modelo && modelos.length > 0) {
      // Verificar que el modelo existe en la lista
      const modeloExiste = modelos.some(m => m.modelo_planeacion === initialParams.modelo)
      if (modeloExiste) {
        fetchInventarioData(initialParams.modelo)
        fetchTablaData(initialParams.modelo)
      }
    }
  }, [initialParams?.compania, initialParams?.modelo, modelos.length])

  // Recargar datos cuando cambie el escenario
  useEffect(() => {
    setOptimizacionTecnoSuper(false)
    if (modeloSlected && companiaSelected) {
      fetchTablaData(modeloSlected, null, true) // preservarFiltros = true
    }
  }, [escenario])

  useEffect(() => {
    setOptimizacionTecnoSuper(false)
  }, [companiaSelected])

  // Recargar inventario y tabla cuando cambien los checkboxes (evita doble carga al montar)
  useEffect(() => {
    if (skipPrototiposEffectOnce.current) {
      skipPrototiposEffectOnce.current = false
      return
    }
    if (modeloSlected && companiaSelected) {
      fetchInventarioData(modeloSlected)
      fetchTablaData(modeloSlected, null, true)
    }
  }, [prototipos, segundas])

  // Handlers
  const handleCompaniaChange = (e) => {
    const selectedCompania = e.target.value
    setCompaniaSelected(selectedCompania)
    // Reiniciar modelo cuando cambie la compañía
    setModeloSlected('')
    setInventarioData(null)
    setTablaData([])
    setNivelServicio('')
    setMesLlegada('')
    setCargabilidad('')
    setFobPorMes({})
    setTipoPeriodo('mes')
  }

  const handleTipoPeriodoChange = (e) => {
    const selectedTipoPeriodo = e.target.value
    setTipoPeriodo(selectedTipoPeriodo)
    // Recargar datos de la tabla con el nuevo período
    if (modeloSlected && companiaSelected) {
      fetchTablaData(modeloSlected, selectedTipoPeriodo, true) // preservarFiltros = true
    }
  }

  const handleModeloChange = (e) => {
    const selectedModelo = e.target.value
    setModeloSlected(selectedModelo)
    if (selectedModelo && companiaSelected) {
      fetchInventarioData(selectedModelo)
      fetchTablaData(selectedModelo)
    } else {
      setInventarioData(null)
      setTablaData([])
      setCargabilidad('')
      setFobPorMes({})
    }
  }

  const handleNextModelo = () => {
    console.log('handleNextModelo')
    const currentIndex = modelos.findIndex(modelo => modelo.modelo_planeacion === modeloSlected)
    console.log('currentIndex', currentIndex)
    if (currentIndex < modelos.length - 1) {
      const selectedModelo = (modelos[currentIndex + 1].modelo_planeacion)
      setModeloSlected(selectedModelo)

      if (selectedModelo && companiaSelected) {
        fetchInventarioData(selectedModelo)
        fetchTablaData(selectedModelo)
      } else {
        setInventarioData(null)
        setTablaData([])
      }
      
    }
  }

  const handlePreviousModelo = () => {
    console.log('handlePreviousModelo')
    const currentIndex = modelos.findIndex(modelo => modelo.modelo_planeacion === modeloSlected)
    if (currentIndex > 0) {
      const selectedModelo = (modelos[currentIndex - 1].modelo_planeacion)
      if (selectedModelo && companiaSelected) {
      setModeloSlected(selectedModelo)
        fetchInventarioData(selectedModelo)
        fetchTablaData(selectedModelo)
      } else {
        setInventarioData(null)
        setTablaData([])
      }
    }
  }

  const handleOptimizarInventario = async () => {
    if (!modeloSlected || !nivelServicio || !mesLlegada) {
      setAlertMessage(
        'Por favor selecciona modelo, nivel de servicio y mes llegada',
      )
      setShowAlert(true)
      return
    }

    try {
      const response = await calcularGuardarInventarioMensual(
        modeloSlected,
        nivelServicio,
        mesLlegada,
        escenario
      )

      if (response) {
        setAlertMessage('Optimización realizada exitosamente')
        setShowAlert(true)
        await fetchTablaData(modeloSlected, null, true) // preservarFiltros = true
      } else {
        setAlertMessage('Error al realizar la optimización')
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error en optimización:', error)
      setAlertMessage('Error al realizar la optimización')
      setShowAlert(true)
    }
  }

  const handleCloseAlert = () => {
    setShowAlert(false)
  }

  // Handlers para modal de llegadas
  const handleOpenLlegadasModal = () => {
    setIsLlegadasModalOpen(true)
  }

  const handleCloseLlegadasModal = () => {
    setIsLlegadasModalOpen(false)
  }

  const handleActualizarCargabilidad = async (nuevaCargabilidad) => {
    if (!modeloSlected || !nuevaCargabilidad) {
      setAlertMessage('Por favor ingrese un valor de cargabilidad válido')
      setShowAlert(true)
      return
    }

    try {
      const response = await actualizarCargabilidadCompra(
        modeloSlected,
        nuevaCargabilidad,
        companiaSelected,
        escenario
      )

      if (response && response.success) {
        setAlertMessage(response.message || 'Cargabilidad actualizada exitosamente')
        setShowAlert(true)
        setCargabilidad(nuevaCargabilidad)
        // Recargar los datos de la tabla para obtener la cargabilidad actualizada
        await fetchTablaData(modeloSlected, null, true)
      } else {
        setAlertMessage(response?.message || 'Error al actualizar la cargabilidad')
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error al actualizar cargabilidad:', error)
      setAlertMessage('Error al actualizar la cargabilidad')
      setShowAlert(true)
    }
  }

  const handleToggleOptimizacionTecnoSuper = async () => {
    if (!companiaSelected || !escenario) {
      setAlertMessage('Selecciona compañía y escenario')
      setShowAlert(true)
      return
    }

    const nuevoActivo = !optimizacionTecnoSuper
    setLoadingOptimizacionTecnoSuper(true)
    try {
      const response = await setEscenarioCaracteristica({
        escenario,
        compania: companiaSelected,
        caracteristica: 'kardex_tecnosuper',
        activo: nuevoActivo,
      })

      if (response?.success) {
        setOptimizacionTecnoSuper(nuevoActivo)
        setAlertMessage(
          response.message ||
            (nuevoActivo
              ? 'Optimización tecno super activada'
              : 'Optimización tecno super desactivada'),
        )
        setShowAlert(true)
        if (modeloSlected) {
          await fetchTablaData(modeloSlected, null, true)
        }
      } else {
        setAlertMessage(
          response?.message ||
            'Error al actualizar la optimización tecno super',
        )
        setShowAlert(true)
      }
    } catch (error) {
      console.error('Error al actualizar optimización tecno super:', error)
      setAlertMessage('Error al actualizar la optimización tecno super')
      setShowAlert(true)
    } finally {
      setLoadingOptimizacionTecnoSuper(false)
    }
  }

  const fobLlegadasUnidadesTotales = useMemo(() => {
    if (!mesLlegada) return null
    const [anio, mes] = mesLlegada.split('-')
    const valor = fobPorMes[`${Number(mes)}-${Number(anio)}`]
    if (valor === undefined || valor === null || valor === '') return null
    const numero = Number(valor)
    return Number.isNaN(numero) ? null : numero
  }, [mesLlegada, fobPorMes])

  return {
    // Estados
    showAlert,
    alertMessage,
    companiaSelected,
    companiasDisponibles,
    modelos,
    loadingModelos,
    modeloSlected,
    inventarioData,
    loadingInventario,
    tablaData,
    loadingTabla,
    nivelServicio,
    mesLlegada,
    fobLlegadasUnidadesTotales,
    cargabilidad,
    mesesColumnas,
    tipoPeriodo,
    prototipos,
    segundas,
    tiposPeriodoDisponibles,
    isLlegadasModalOpen,
    optimizacionTecnoSuper,
    loadingOptimizacionTecnoSuper,
    
    // Setters
    setCompaniaSelected,
    setNivelServicio,
    setMesLlegada,
    setTipoPeriodo,
    setPrototipos,
    setSegundas,
    
    // Handlers
    handleCompaniaChange,
    handleModeloChange,
    handleTipoPeriodoChange,
    handleOptimizarInventario,
    handleActualizarCargabilidad,
    handleCloseAlert,
    handleOpenLlegadasModal,
    handleCloseLlegadasModal,
    handleNextModelo,
    handlePreviousModelo,
    handleToggleOptimizacionTecnoSuper,
    
    // Funciones para recargar datos
    refetchTablaData: () => fetchTablaData(modeloSlected, null, true), // preservarFiltros = true
  }
} 